import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Nav, Button, Navbar } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../../images/logo.svg';
import BackgroundParticles from './BackgroundParticles';
import NavBackground from '../NavBackground/NavBackground';
import './Banner.css';
import { useLanguage } from '../../context/LanguageContext';

interface BannerProps {
  showMainContent?: boolean;
}

const Banner: React.FC<BannerProps> = ({ showMainContent = true }) => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [bannerInView, setBannerInView] = useState(true);
  const bannerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver to detect if banner is in view
  useEffect(() => {
    const ref = bannerRef.current;
    if (!ref) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setBannerInView(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, []);

  // Helper to scroll to a section by id, navigating home if needed
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to top of the page (banner), navigating home if needed
  const scrollToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Update navItems to scroll to sections
  const navItems = [
    { title: t('nav.home'), action: scrollToTop },
    { title: t('nav.about'), action: () => scrollToSection('about') },
    { title: t('nav.industries'), action: () => scrollToSection('industries-section') },
    { title: t('nav.services'), action: () => scrollToSection('services-section') },
    { title: t('nav.clients'), action: () => scrollToSection('clientes-section') },
    { title: t('nav.contact'), action: () => scrollToSection('contact-section') },
  ];

  const handleNavClick = () => {
    setExpanded(false);
  };

  const handleToggle = (isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Container fluid className={`p-0 banner-container ${!showMainContent ? 'slim' : ''}`} ref={bannerRef}>
      <Row className="g-0">
        <Col className="position-relative">
          <div className="banner-background">
            {isHomePage && <BackgroundParticles isSlim={!showMainContent} />}
          </div>
          
          <Navbar 
            expand="lg" 
            className={`sticky-nav ${isScrolled ? 'scrolled' : ''} ${showMainContent ? 'home' : 'inner-page'} ${expanded ? 'menu-expanded' : ''}`}
            expanded={expanded}
            onToggle={handleToggle}
          >
            <div className="nav-background-container">
              <NavBackground frozen={!bannerInView} />
              <div className="nav-blur-overlay" />
            </div>
            <Container fluid className="px-4 position-relative">
              <Navbar.Brand 
                className="nav-font" 
                onClick={() => navigate('/')}
                style={{ cursor: 'pointer', height: '100%' }}
              >
                <img 
                  src={logo} 
                  alt="Lucods"
                  className="brand-logo"
                />
              </Navbar.Brand>
              <Navbar.Toggle aria-controls="basic-navbar-nav" className="custom-toggler" />
              <Navbar.Collapse id="basic-navbar-nav">
                <Nav className="ms-auto nav-font">
                  {navItems.map((item) => (
                    <Nav.Link 
                      key={item.title}
                      onClick={() => {
                        item.action();
                        handleNavClick();
                      }}
                      className="text-white mx-2"
                    >
                      {item.title}
                    </Nav.Link>
                  ))}
                </Nav>
              </Navbar.Collapse>
            </Container>
          </Navbar>

          {/* Main Content - Only show on home page */}
          {showMainContent && (
            <div className="banner-content">
              <h2 className="nav-font banner-title">{t('banner.title')}</h2>
              <h2 className="nav-font banner-title2">{t('banner.title2')}</h2>
              <p className="nav-font banner-subtitle">{t('banner.subtitle')}</p>
              <a
                href="https://calendly.com/nico-lucods/30min"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <Button className="text-font banner-button">
                  {t('banner.schedule')}
                </Button>
              </a>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Banner;